import { calculateObjectMeasurements } from "../geometry/measurements.js";
import { getGridLineValues, getGridSpacingDocumentUnits, screenToWorld, worldToScreen } from "../geometry/coordinates.js";

export class CanvasRenderer {
  constructor(config) {
    this.config = config;
    this.canvas = null;
    this.context = null;
  }

  mount(container) {
    this.canvas = document.createElement("canvas");
    this.canvas.width = this.config.canvas.width;
    this.canvas.height = this.config.canvas.height;
    this.canvas.style.width = "100%";
    this.canvas.style.maxWidth = `${this.config.canvas.width}px`;
    this.canvas.style.background = this.config.canvas.background;
    this.canvas.dataset.renderer = "canvas";
    container.appendChild(this.canvas);
    this.context = this.canvas.getContext("2d");
  }

  setConfig(config) {
    this.config = config;

    if (!this.canvas) {
      return;
    }

    this.canvas.width = this.config.canvas.width;
    this.canvas.height = this.config.canvas.height;
    this.canvas.style.maxWidth = `${this.config.canvas.width}px`;
    this.canvas.style.background = this.config.canvas.background;
  }

  render(documentModel, options = {}) {
    if (!this.context || !this.canvas) {
      return;
    }

    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);

    const renderOptions = this.getRenderOptions(options);
    const dotsById = new Map(documentModel.dots.map((dot) => [dot.id, dot]));
    const sortedObjects = [...documentModel.objects].sort((left, right) => (left.zIndex ?? 0) - (right.zIndex ?? 0));

    this.drawGrid(renderOptions);

    for (const dot of documentModel.dots) {
      this.drawDot(dot, renderOptions.viewport);
    }

    for (const object of sortedObjects) {
      if (object.visible === false) {
        continue;
      }

      if (object.type === "line") {
        this.drawLine(object, dotsById, renderOptions.viewport);
      }

      if (object.type === "rectangle") {
        this.drawRectangle(object, dotsById, renderOptions.viewport);
      }
    }

    this.drawPreview(renderOptions);
    this.drawMeasurementLabels(sortedObjects, dotsById, renderOptions);
  }

  drawDot(dot, viewport) {
    const radius = dot.meta?.radius ?? 4;
    const point = worldToScreen(dot, viewport);

    this.context.beginPath();
    this.context.arc(point.x, point.y, radius, 0, Math.PI * 2);
    this.context.fillStyle = dot.meta?.color || "#cc5500";
    this.context.fill();
  }

  drawLine(object, dotsById, viewport) {
    const [start, end] = this.resolveObjectPoints(object, dotsById);

    if (!start || !end) {
      return;
    }

    const screenStart = worldToScreen(start, viewport);
    const screenEnd = worldToScreen(end, viewport);

    this.context.save();
    this.context.beginPath();
    this.context.moveTo(screenStart.x, screenStart.y);
    this.context.lineTo(screenEnd.x, screenEnd.y);
    this.context.strokeStyle = object.strokeColor || "#111111";
    this.context.lineWidth = object.lineWidth || 2;
    this.context.globalAlpha *= object.opacity ?? 1;
    this.context.stroke();
    this.context.restore();
  }

  drawRectangle(object, dotsById, viewport) {
    const points = this.resolveObjectPoints(object, dotsById).slice(0, 4);

    if (points.length < 4 || points.some((point) => !point)) {
      return;
    }

    const screenPoints = points.map((point) => worldToScreen(point, viewport));

    this.context.save();
    this.context.lineWidth = object.lineWidth || 2;
    this.context.strokeStyle = object.strokeColor || "#111111";
    this.context.fillStyle = object.fillColor || "transparent";
    this.context.globalAlpha *= object.opacity ?? 1;
    this.context.beginPath();
    this.context.moveTo(screenPoints[0].x, screenPoints[0].y);

    for (const point of screenPoints.slice(1)) {
      this.context.lineTo(point.x, point.y);
    }

    this.context.closePath();
    this.context.fill();
    this.context.stroke();
    this.context.restore();
  }

  resolveObjectPoints(object, dotsById) {
    const pointIds = Array.isArray(object.points) ? object.points : Array.isArray(object.dotIds) ? object.dotIds : [];
    return pointIds.map((pointId) => dotsById.get(pointId) ?? null);
  }

  drawGrid({ viewport, coordinates, grid }) {
    const gridVisible = Boolean(grid.visible || this.config.canvas.showGrid);

    if (!gridVisible) {
      return;
    }

    const spacing = getGridSpacingDocumentUnits(grid, coordinates);
    const topLeft = screenToWorld({ x: 0, y: 0 }, viewport);
    const bottomRight = screenToWorld({ x: this.canvas.width, y: this.canvas.height }, viewport);
    const bounds = {
      minX: Math.min(topLeft.x, bottomRight.x),
      maxX: Math.max(topLeft.x, bottomRight.x),
      minY: Math.min(topLeft.y, bottomRight.y),
      maxY: Math.max(topLeft.y, bottomRight.y)
    };

    if (grid.subdivisions > 1) {
      this.drawGridLines({
        viewport,
        bounds,
        spacing: spacing / grid.subdivisions,
        color: grid.subgridColor,
        lineWidth: grid.subgridLineWidth,
        skipSpacing: spacing
      });
    }

    this.drawGridLines({
      viewport,
      bounds,
      spacing,
      color: grid.color,
      lineWidth: grid.lineWidth
    });
  }

  drawGridLines({ viewport, bounds, spacing, color, lineWidth, skipSpacing }) {
    const xValues = limitGridLines(getGridLineValues(bounds.minX, bounds.maxX, spacing));
    const yValues = limitGridLines(getGridLineValues(bounds.minY, bounds.maxY, spacing));

    if (xValues.length === 0 && yValues.length === 0) {
      return;
    }

    this.context.save();
    this.context.beginPath();
    this.context.strokeStyle = color;
    this.context.lineWidth = lineWidth;

    for (const x of xValues) {
      if (skipSpacing && isMultipleOfSpacing(x, skipSpacing)) {
        continue;
      }

      const screenX = worldToScreen({ x, y: 0 }, viewport).x;
      this.context.moveTo(screenX, 0);
      this.context.lineTo(screenX, this.canvas.height);
    }

    for (const y of yValues) {
      if (skipSpacing && isMultipleOfSpacing(y, skipSpacing)) {
        continue;
      }

      const screenY = worldToScreen({ x: 0, y }, viewport).y;
      this.context.moveTo(0, screenY);
      this.context.lineTo(this.canvas.width, screenY);
    }

    this.context.stroke();
    this.context.restore();
  }

  drawMeasurementLabels(objects, dotsById, { viewport, coordinates, measurements }) {
    if (!measurements.visible) {
      return;
    }

    for (const object of objects) {
      const objectMeasurements = calculateObjectMeasurements(object, dotsById, {
        coordinates,
        displayUnit: measurements.displayUnit,
        precision: measurements.precision
      });

      for (const measurement of objectMeasurements) {
        this.drawMeasurementLabel(measurement, viewport, measurements);
      }
    }
  }

  drawPreview({ viewport, preview }) {
    if (!preview) {
      return;
    }

    const dots = Array.isArray(preview.dots) ? preview.dots : [];
    const objects = Array.isArray(preview.objects) ? preview.objects : [];
    const dotsById = new Map(dots.map((dot) => [dot.id, dot]));

    this.context.save();
    this.context.globalAlpha *= preview.opacity ?? 0.72;

    for (const dot of dots) {
      this.drawDot(dot, viewport);
    }

    for (const object of objects) {
      if (object.type === "line") {
        this.drawLine(object, dotsById, viewport);
      }

      if (object.type === "rectangle") {
        this.drawRectangle(object, dotsById, viewport);
      }
    }

    this.context.restore();
  }

  drawMeasurementLabel(measurement, viewport, measurements) {
    const start = worldToScreen(measurement.start, viewport);
    const end = worldToScreen(measurement.end, viewport);
    const x = (start.x + end.x) / 2;
    const y = (start.y + end.y) / 2;
    const paddingX = 5;
    const paddingY = 3;

    this.context.save();
    this.context.font = "12px Trebuchet MS, Segoe UI, sans-serif";
    this.context.textBaseline = "middle";

    const metrics = this.context.measureText(measurement.label);
    const width = metrics.width + paddingX * 2;
    const height = 18;

    this.context.fillStyle = measurements.labelBackground;
    this.context.fillRect(x - width / 2, y - height / 2, width, height);
    this.context.fillStyle = measurements.labelColor;
    this.context.fillText(measurement.label, x - metrics.width / 2, y + paddingY - 1);
    this.context.restore();
  }

  getRenderOptions(options) {
    return {
      viewport: options.viewport ?? { zoom: 1, panX: 0, panY: 0 },
      coordinates: options.coordinates ?? this.config.coordinates,
      grid: {
        ...(this.config.grid ?? {}),
        ...(options.grid ?? {})
      },
      measurements: {
        ...(this.config.measurements ?? {}),
        ...(options.measurements ?? {})
      },
      preview: options.preview ?? null
    };
  }
}

function isMultipleOfSpacing(value, spacing) {
  return Math.abs(value / spacing - Math.round(value / spacing)) < 0.000001;
}

function limitGridLines(values) {
  const maxGridLines = 2000;

  if (values.length > maxGridLines) {
    return [];
  }

  return values;
}
